import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Truck, AlertTriangle, CalendarClock, Phone, MessageCircle } from "lucide-react";

function DeliveryRow({ o }) {
  const phoneDigits = (o.source_phone || "").replace(/[^0-9]/g, "");
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 p-4 hover:bg-zinc-50">
      <Link to={`/orders/${o.id}`} className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-zinc-500">{o.order_no}</span>
          <StatusBadge status={o.status} />
          <PriorityBadge priority={o.priority} />
        </div>
        <div className="font-medium text-sm mt-1">{o.title}</div>
        <div className="text-xs text-zinc-500">
          {o.customer_name || "—"} · Due {new Date(o.expected_delivery).toLocaleString()}
        </div>
      </Link>
      <div className="flex items-center gap-1 justify-end">
        {o.source_phone && (
          <>
            <a href={`tel:${o.source_phone}`}>
              <Button size="icon" variant="outline" className="h-8 w-8"><Phone className="w-3.5 h-3.5" /></Button>
            </a>
            <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noreferrer">
              <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600"><MessageCircle className="w-3.5 h-3.5" /></Button>
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, list, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="font-heading font-bold text-xl tracking-tight mb-3 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${accent}`} />
        <Icon className="w-4 h-4" />
        {title}
        <span className="text-zinc-400 num font-medium">({list.length})</span>
      </h3>
      {list.length === 0 ? (
        <div className="text-sm text-zinc-500 mb-6">Nothing here.</div>
      ) : (
        <Card className="p-0 overflow-hidden mb-8">
          <div className="divide-y divide-border">
            {list.map((o) => <DeliveryRow key={o.id} o={o} />)}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function Deliveries() {
  const [data, setData] = useState({ today: [], upcoming: [], overdue: [] });

  useEffect(() => {
    api.get("/deliveries").then((r) => setData(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-zinc-500">Schedule</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">
          Follow-ups & Deliveries
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          All orders grouped by their expected delivery date. Reminders also pop up on the dashboard a day before delivery.
        </p>
      </div>
      <Section title="Overdue" list={data.overdue} accent="bg-red-500" icon={AlertTriangle} testid="deliveries-overdue" />
      <Section title="Today" list={data.today} accent="bg-yellow-500" icon={Truck} testid="deliveries-today" />
      <Section title="Upcoming" list={data.upcoming} accent="bg-emerald-500" icon={CalendarClock} testid="deliveries-upcoming" />
    </div>
  );
}
